"use client"
import {observer} from "mobx-react";
import {useSearchParams} from "next/navigation";
import {useEffect, useRef, useState} from "react";
import {useStore} from "@/store";
import styled from "styled-components";
import {
    FileUploadArea,
    NoDataList,
    RequiredStar,
    TextAreaWrap,
    TitleContentInner,
    TitleContentWrap
} from "@/styles/CommonStyle";
import SelectBox from "@/components/common/SelectBox";
import Input from "@/components/common/Input";
import {fileUpload, isEmail} from "@/utils";
import {BsPaperclip} from "react-icons/bs";
import {IoCloseCircleOutline} from "react-icons/io5";
import {SIZES} from "@/components/constants";
import Button from "@/components/common/Button";
import FormConfirm from "@/app/homepage/form/components/FormConfirm";
import {getCurrentNode} from "@/utils/menu";

const FormTypeBoard = observer(() => {
    const {contentStore, navigationStore, loadingStore} = useStore();
    const searchParams = useSearchParams();
    const [writeMailSelect, setWriteMailSelect] = useState("write");
    const [confirmState, setConfirmState] = useState(false);
    const menuId = searchParams.get("menu-id");
    const hideFiles = useRef(null);
    useEffect(() => {
        setWriteMailSelect("write");
        contentStore.seFormTypeClear();
        contentStore.setMenuContent(navigationStore.navigateList, menuId, undefined).then();
    }, [menuId]);
    useEffect(() => {
        return() => {
            contentStore.seFormTypeClear();
        }
    }, []);

    const setFileUpload = () => {
        hideFiles.current.click();
    }

    const setMakeConvertFormContent = () => {
        const result = { ...contentStore.formDataMap };
        const rows = {};
        for (const [key, val] of Object.entries(contentStore.formDataMap)) {
            const parts = key.split("-");
            if (parts.length !== 3) continue;
            const [kind, part, rowNo] = parts;
            if (kind === "tel" && part.startsWith("p")) {
                const idx = parseInt(part.slice(1), 10) - 1;
                (rows[rowNo] ??= { email: [], tel: [] }).tel[idx] = val ?? "";
                delete result[key];
                const items = rows[rowNo];
                const final = (items.email.length ? items.email.join("@") : items.tel.join("-")) || "";
                if (final) result[`row${rowNo}`] = final;
            } else if (kind === "email" && part.startsWith("e")) {
                const idx = parseInt(part.slice(1), 10) - 1;
                (rows[rowNo] ??= { email: [], tel: [] }).email[idx] = val ?? "";
                delete result[key];
                const items = rows[rowNo];
                const final = (items.email.length ? items.email.join("@") : items.tel.join("-")) || "";
                if (final) result[`row${rowNo}`] = final;
            }
        }
        for (const [resultKey, resultVal] of Object.entries(result)) {
            const val = typeof resultVal === "string" ? resultVal.trim() : resultVal;
            if (val === "checked" || val === true) {
                delete result[resultKey];
            }
        }
        return result;
    }




    const formValidateRequired = () => {
        if (contentStore.currentDataInfo?.form && contentStore.currentDataInfo.form.length > 0) {
            const isEmpty = (value) => value == null || (typeof value === "string" && value.trim() === "");
            const requiredFields = contentStore.currentDataInfo.form.filter(field => field.required);
            const missingNames = [];
            let needAgreeMsg = false;

            for (const item of requiredFields) {
                const id = item._id;

                if (item.type === "email") {
                    const e1 = contentStore.formDataMap[`email-e1-${id}`];
                    const e2 = contentStore.formDataMap[`email-e2-${id}`];
                    if (isEmpty(e1) || isEmpty(e2)) {
                        if (!missingNames.includes(item.name)) missingNames.push(item.name);
                        continue;
                    }
                    const emailValue = `${e1}@${e2}`;
                    if (!isEmail(emailValue)) {
                        alert(`${item.name} 항목의 이메일 형식이 올바르지 않습니다.`);
                        return false;
                    }
                    continue;
                }
                if (item.type === "tel") {
                    const p1 = contentStore.formDataMap[`tel-p1-${id}`];
                    const p2 = contentStore.formDataMap[`tel-p2-${id}`];
                    const p3 = contentStore.formDataMap[`tel-p3-${id}`];
                    if (isEmpty(p1) || isEmpty(p2) || isEmpty(p3)) {
                        if (!missingNames.includes(item.name)) missingNames.push(item.name);
                        continue;
                    }
                    if (String(p1).length <= 1 || String(p2).length <= 2 || String(p3).length <= 3) {
                        alert("휴대전화 형식이 올바르지 않습니다.");
                        return false;
                    }
                    continue;
                }
                if (item.type === "content") {
                    const value = contentStore.formDataMap[`row${id}`];
                    if (isEmpty(value)) {
                        if (!missingNames.includes(item.name)) missingNames.push(item.name);
                    }
                    continue;
                }
                if (item.type === "agree") {
                    const value = contentStore.formDataMap[`row${id}`];
                    if (!value) needAgreeMsg = true;
                    continue;
                }
                const key = `row${id}`;
                const val = contentStore.formDataMap[key] ?? (item.type === "select" ? item.selected : undefined);
                if (isEmpty(val)) {
                    if (!missingNames.includes(item.name)) missingNames.push(item.name);
                }
            }

            if (needAgreeMsg) {
                alert("개인정보에 동의해 주세요.");
                return false;
            }

            if (missingNames.length > 0) {
                alert(`다음 필수 항목을 입력해 주세요: ${missingNames.join(", ")}`);
                return false;
            }

            return true;
        }
    }





    return(
        <>
            {
                contentStore.currentDataInfo?.form && contentStore.currentDataInfo.form.length > 0 ?
                    <FormTypeBoardWrap>

                        <TitleContentWrap>
                            {
                                contentStore.currentDataInfo.form.map((item, key) =>
                                    <TitleContentInner className={`form-list ${item.type === "agree" ? "agree" : ""}`}
                                                       key={key}>
                                        {
                                            item.type !== "agree" &&
                                            <div className={"title"}>
                                                {item.name}
                                                {
                                                    item.required &&
                                                    <RequiredStar>*</RequiredStar>
                                                }
                                            </div>
                                        }
                                        <div className={`content ${item.type === "agree" ? "agree" : ""}`}>
                                            {
                                                item.type === "select" ?
                                                    <div className={"select-wrap"}>
                                                        <SelectBox
                                                            option={item.options}
                                                            full={true}
                                                            type="solid"
                                                            round={true}
                                                            placeholder="선택"
                                                            onChange={async (e) => {
                                                                contentStore.setFormDataUpDate(e.value, item._id)
                                                            }}
                                                            value={item.options.find(opt => opt.value === contentStore.formDataMap[`row${item._id}`]) || null}
                                                        />
                                                    </div>
                                                :
                                                item.type === "text" ?
                                                    <div className={"text-wrap"}>
                                                        <Input
                                                            inputType="text"
                                                            placeholder="입력해주세요"
                                                            width={"fill"}
                                                            value={contentStore.formDataMap[`row${item._id}`]}
                                                            onChange={(e) => {
                                                                contentStore.setFormDataUpDate(e.target.value, item._id)
                                                            }}
                                                        />
                                                    </div>
                                                :
                                                item.type === "tel" ?
                                                    <div className={"tel-wrap"}>
                                                        <div className={"tel-select-wrap"}>
                                                            <div className={"tel-select-list"}>
                                                                <Input
                                                                    inputType="tel"
                                                                    placeholder="숫자 입력"
                                                                    inputMode={"numeric"}
                                                                    width={"fill"}
                                                                    value={contentStore.formDataMap[`${item.type}-p1-${item._id}`]}
                                                                    onChange={(e) => {
                                                                        contentStore.setFormDataUpDate(e.target.value.replace(/\D/g, '').slice(0, 3), item._id, "p1")
                                                                    }}
                                                                />
                                                            </div>
                                                            <span>-</span>
                                                        </div>
                                                        <div className={"tel-detail"}>
                                                            <Input
                                                                inputType="tel"
                                                                placeholder="숫자 입력"
                                                                inputMode={"numeric"}
                                                                width={"fill"}
                                                                value={contentStore.formDataMap[`${item.type}-p2-${item._id}`]}
                                                                onChange={(e) => {
                                                                    contentStore.setFormDataUpDate(e.target.value.replace(/\D/g, '').slice(0, 4), item._id, "p2")
                                                                }}
                                                            />
                                                            <span>-</span>
                                                            <Input
                                                                inputType="tel"
                                                                placeholder="숫자 입력"
                                                                inputMode={"numeric"}
                                                                width={"fill"}
                                                                value={contentStore.formDataMap[`${item.type}-p3-${item._id}`]}
                                                                onChange={(e) => {
                                                                    contentStore.setFormDataUpDate(e.target.value.replace(/\D/g, '').slice(0, 4), item._id, "p3")
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                :
                                                item.type === "email" ?
                                                    <div className={"mail-wrap"}>
                                                        <div className={"mail-input-wrap"}>
                                                            <div className={"mail-id-wrap"}>
                                                                <Input
                                                                    inputType="text"
                                                                    placeholder="입력해주세요"
                                                                    width={"fill"}
                                                                    value={contentStore.formDataMap[`${item.type}-e1-${item._id}`]}
                                                                    onChange={(e) => {
                                                                        contentStore.setFormDataUpDate(e.target.value, item._id, "e1")
                                                                    }}
                                                                />
                                                                <span>@</span>
                                                            </div>
                                                            <Input
                                                                inputType="text"
                                                                placeholder={`${writeMailSelect === "write" ? "직접입력해 주세요." : "메일을 선택해 주세요."}`}
                                                                width={"fill"}
                                                                readOnly={writeMailSelect !== "write"}
                                                                value={contentStore.formDataMap[`${item.type}-e2-${item._id}`]}
                                                                onChange={(e) => {
                                                                    contentStore.setFormDataUpDate(e.target.value, item._id, "e2")
                                                                }}
                                                            />
                                                        </div>
                                                        <SelectBox
                                                            option={EMAIL_SORT_LIST}
                                                            full={true}
                                                            type="solid"
                                                            round={true}
                                                            placeholder="선택"
                                                            onChange={async (e) => {
                                                                if(e.value !== "write"){
                                                                    contentStore.setFormDataUpDate(e.value, item._id, "e2")
                                                                } else {
                                                                    contentStore.setFormDataUpDate("", item._id, "e2")
                                                                }
                                                                setWriteMailSelect(e.value)
                                                            }}
                                                            value={EMAIL_SORT_LIST.find(opt => opt.value === contentStore.formDataMap[`${item.type}-e2-${item._id}`]) || EMAIL_SORT_LIST[0]}
                                                        />
                                                    </div>
                                                :
                                                item.type === "files" ?
                                                    <FileUploadArea>
                                                        <div className={"file-wrap"}
                                                             onClick={setFileUpload}
                                                        >


                                                            <input
                                                                className={"file-hide-input"}
                                                                ref={hideFiles}
                                                                type="file"
                                                                multiple
                                                                accept=".jpg,.jpeg,.png,.pdf,.gif,.xls,.xlsx,.hwp,.doc,.docx,.ppt,.pptx,.txt,.hwpx"
                                                                onChange={(e) =>
                                                                    fileUpload(e, {
                                                                        prevFiles: contentStore.formFile,
                                                                        onAfter: contentStore.setFormFiles,
                                                                        maxSizeMB: parseInt(contentStore.currentDataInfo.options.file.size.split("MB")[0]),
                                                                        multiple: true,
                                                                        maxFiles: contentStore.currentDataInfo.options.file.count,
                                                                    })
                                                                }
                                                            />
                                                            <div className={"file-input-wrap"}>
                                                                <Input
                                                                    inputType="text"
                                                                    placeholder="gif,jpg,jpeg,png,xls,xlsx,hwp,doc,docx,ppt,pptx,txt,pdf,hwpx"
                                                                    width={"fill"}
                                                                    readOnly={true}
                                                                />
                                                                <div className={"file-search-btn"}>
                                                                    <BsPaperclip size={24} color="#999"/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {contentStore.formFile && contentStore.formFile.length > 0 && (
                                                            <div className={"file-list-info"}>
                                                                {contentStore.formFile.map((item, idx) => (
                                                                    <div className={"file-item-wrap"} key={idx}>
                                                                        <div
                                                                            className={"cancel-btn"}
                                                                            onClick={() => contentStore.removeFormFile(idx)}   // ← 삭제 연결
                                                                        >
                                                                            <IoCloseCircleOutline size={20} color={"#999"} />
                                                                        </div>
                                                                        <div className={"file-item-name"}>{item.name}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </FileUploadArea>
                                                :
                                                item.type === "content" ?
                                                    <div className={"content-wrap"}>
                                                        <TextAreaWrap className={"rd"}>
                                                            <textarea
                                                                value={contentStore.formDataMap[`row${item._id}`]}
                                                                onChange={(e) => {
                                                                    contentStore.setFormDataUpDate(e.target.value, item._id)
                                                                }}
                                                                placeholder="내용을 입력해주세요"
                                                            />
                                                        </TextAreaWrap>
                                                    </div>
                                                :
                                                item.type === "agree" ?
                                                    <div className={"agree-wrap"}>
                                                        <TextAreaWrap>
                                                            <textarea
                                                                value={item.value}
                                                                readOnly={true}
                                                            />
                                                        </TextAreaWrap>
                                                        <div className={"label-chk-wrap"}>
                                                            <Input
                                                                inputType="checkbox"
                                                                id={"list-all-check"}
                                                                labelName={"동의"}
                                                                checked={contentStore.formDataMap[`row${item._id}`] === "checked"}
                                                                onChange={(e) => {
                                                                    const isCheckedText = e.target.checked ? "checked" : "";
                                                                    contentStore.setFormDataUpDate(isCheckedText, item._id)
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                :
                                                <></>
                                            }
                                        </div>
                                    </TitleContentInner>
                                )
                            }
                        </TitleContentWrap>

                        <div className={"submit-btn"}>
                            <Button
                                type="fill"
                                size="xl"
                                text="제출 하기"
                                brand={true}
                                onClickFunc={async () => {
                                    const formValidate = formValidateRequired();
                                    if(!formValidate){
                                        return;
                                    }
                                    try {
                                        loadingStore.setLoadingState(true);
                                        const root = getCurrentNode(navigationStore.navigateList, menuId)?.trail[0];
                                        const contentData = setMakeConvertFormContent();
                                        const formData = await contentStore.setSaveFormData(menuId, root.menuId, contentData);
                                        if(formData){
                                            setConfirmState(true);
                                            setWriteMailSelect("write");
                                            contentStore.seFormTypeClear("save");
                                            loadingStore.setLoadingState(false);
                                        }
                                    } catch (e) {
                                        console.log(e)
                                        loadingStore.setLoadingState(false);
                                    }

                                }}
                            />
                        </div>
                    </FormTypeBoardWrap>
                    :
                    <NoDataList>확인 불가능한 페이지 입니다.</NoDataList>
            }
            <FormConfirm active={confirmState} setConfirmState={setConfirmState}/>
        </>
    )
});


const FormTypeBoardWrap = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px 0 180px;

    .content {
        &.agree {
            display: block;
            gap: 0;
            padding: 0;
        }
    }

    .form-list {
        &.agree {
            min-height: auto;
            border: none;
        }
    }

    .input-wrapper{
        border: 1px solid #D1D1D1;
    }
    .react-select__menu{
        width: 100%;
    }
    .select-wrap{
        width: 100%;
    }
    .react-select__control {
        height: 44px;
        border-radius: 8px;
        border: 1px solid #D1D1D1;
    }
    .text-wrap{
        width: 100%;
    }
    
    .tel-wrap{
        display: flex;
        align-items: center;
        gap: 4px;
        .tel-select-wrap{
            display: flex;
            align-items: center;
            gap: 4px;
            .tel-select-list{
                width: 102px;
            }
        }
        .tel-detail{
            display: flex;
            align-items: center;
            gap: 4px;
            .input-wrapper{
                width: 102px;
            }
        }
    }
    .mail-wrap{
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
        .mail-input-wrap{
            display: flex;
            align-items: center;
            gap: 4px;
            .mail-id-wrap{
                display: flex;
                align-items: center;
                gap: 4px;
                width: 100%;
            }
        }
    }
    
    .content-wrap{
        width: 100%;
        textarea{
            height: 242px;
            font-size: 16px;
            font-weight: 400;
            
        }
    } 
    .agree-wrap{
        margin-top: 24px;
        textarea {
            height: 160px;
            font-size: 16px;
            font-weight: 400;
        }
        .label-chk-wrap{
            padding: 8px 4px;
            .input-wrapper{
                border: none;
            }
        }
    }
    .submit-btn{
        margin-top: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    @media (max-width: ${SIZES.TABLET}) {
        padding: 40px 16px 80px;
        max-width: 100%;
        width: 100%;
        overflow: hidden;
        .tel-wrap{
            display: block;
            width: 100%;
            .tel-select-wrap{
                margin-bottom: 8px;                
                .tel-select-list{
                    width: 100%;
                }
            }
            .tel-detail{
                display: flex;
                align-items: center;
                gap: 4px;
                .input-wrapper{
                    width: auto;
                }
            }
        }
        .mail-wrap{
            .mail-input-wrap{
                display: block;
                .mail-id-wrap{
                    width: auto;
                    margin-bottom: 8px;
                }
            }
        }
        
        .agree-wrap {
            .label-chk-wrap {
                display: flex;
                justify-content: end;
            }
        }
    }
`
export default FormTypeBoard;
export const EMAIL_SORT_LIST = [
    {label: "직접입력", value: "write"},
    {label: "naver.com", value: "naver.com"},
    {label: "gmail.com", value: "gmail.com"},
    {label: "daum.net", value: "daum.net"},
    {label: "hanmail.net", value: "hanmail.net"},
    {label: "nate.com", value: "nate.com"},
]





const TEL_SORT_LIST = [
    {label: "010", value: "010"},
    {label: "011", value: "011"},
    {label: "02", value: "02"},
    {label: "031", value: "031"},
]
